import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FORMS, MODULES } from "../../../../const";
import {
  CurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../../renderer/StyleRenderer";
import ButtonRenderer from "../../../renderer/ButtonRenderer";
import { ChevronDown, ChevronUp } from "react-feather";
import { getBulkDropdownData, getDropdownData } from "../../../../api/keyvalue";
import { getJobInfo } from "../../../../api/finance/addChargesApi";
import { getChargesByChhIdAndChargeCategoryNotGenerated } from "../../../../api/finance/chargeConfirmation";
import { formatFromJsonDate } from "../../../../dateConfig";
import LoadingSpinner from "../../../LoadingSpinner";
import Input from "../../../input/Input";
import Dropdown from "../../../input/Dropdown";
import InfoList from "../../../InfoList";
import Grid from "../../../Grid";
import CheckboxRenderer from "../../../renderer/CheckboxRenderer";
import Button from "../../../input/Button";
import { useDispatch, useSelector } from "react-redux";
import { invEvActions } from "../../../../store/finance/inv-ev/inv-ev-slice";
import { updateBulkInvEv } from "../../../../store/finance/inv-ev/inv-ev-actions";
import { reportActions } from "../../../../store/report/report-slice";
import { toast } from "react-toastify";
import ChargeTaxBreakdown from "../ChargeTaxBreakdown";
import { addAuditRecord } from "../../../../api/auditApi";
import useUserCompany from "../../../../hooks/useUserCompany";
import { downloadExcelReport } from "../../../../api/releaseNoteApi";

const gridHeaderConst = {
  CHARGE: "CHARGE-HEADER",
};

const HEADER_FORM = FORMS.EV_INV_MANAGER.HEADER.form;

const BulkEvInvComponent = ({
  getGridData,
  formatter,
  username,
  commonNoConsolidationView,
  commonConsolidated,
  openTaxBreakdown,
  openReportModal,
}) => {
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const evInvHeaderPermissions = auth[HEADER_FORM];
  const bulkGenEvPermission = evInvHeaderPermissions?.FORM?.BULK_GEN_EV;
  const bulkGenInvPermission = evInvHeaderPermissions?.FORM?.BULK_GEN_INV;

  const bulkDownloadEvPermission =
    evInvHeaderPermissions?.FORM?.DOWNLOAD_BULK_EV;
  const bulkDownloadInvPermission =
    evInvHeaderPermissions?.FORM?.DOWNLOAD_BULK_INV;

  const gridRef = useRef();

  const [isLoading, setIsLoading] = useState(true);

  // All charge data
  const [bulkChargeData, setBulkChargeData] = useState([]);
  // Charge data with headers
  const [bulkGridData, setBulkGridData] = useState([]);

  const isEditMode = useSelector((state) => state.invEv.editMode);
  const savedUpdateResponse = useSelector(
    (state) => state.invEv.fetchedResponse,
  );

  const selectedJob = useSelector((state) => state.invEv.selectedJob);
  const categoryId = useSelector((state) => state.invEv.categoryId);
  const currentJobIds = useSelector((state) => state.invEv.jobIds);

  const dispatch = useDispatch();
  const company = useUserCompany();
  const user = useSelector((state) => state.user);

  const [bulkJobSummary, setBulkJobSummary] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [selectedCharge, setSelectedCharge] = useState(null);

  const [currentStakeholder, setCurrentStakeholder] = useState("");
  const [consolidatedView, setConsolidatedView] = useState(3);
  const [consolidateOptions, setConsolidateOptions] = useState([]);
  const [consolidateOptionsLoading, setConsolidateOptionsLoading] =
    useState(false);
  const [groupedData, setGroupedData] = useState({});
  const [shipperWiseData, setShipperWiseData] = useState({});
  const [chargeDataChanged, setChargeDataChanged] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const selectAllRef = useRef(selectAll);

  const withoutTaxTotal = useSelector((state) => state.invEv.withoutTaxTotal);
  const withTaxTotal = useSelector((state) => state.invEv.withTaxTotal);
  const taxAmount = useSelector((state) => state.invEv.taxAmount);
  const globalSelectAll = useSelector((state) => state.invEv.taxAmount);

  useEffect(() => {
    setSelectAll(globalSelectAll);
  }, [globalSelectAll]);

  useEffect(() => {
    selectAllRef.current = selectAll;
  }, [selectAll]);

  useEffect(() => {
    setSelectedData(
      bulkGridData.filter(
        (chg) => chg.chdSelected && chg.section !== gridHeaderConst.CHARGE,
      ),
    );
  }, [bulkGridData]);

  useEffect(() => {
    setBulkChargeData((prevState) =>
      prevState.map((chg) => ({ ...chg, chdSelected: selectAll })),
    );
    let selectedShipperWiseData = {};
    for (let [shipper, charges] of Object.entries(shipperWiseData)) {
      selectedShipperWiseData = {
        ...selectedShipperWiseData,
        [shipper]: charges.map((chg) => ({ ...chg, chdSelected: selectAll })),
      };
    }
    setShipperWiseData(selectedShipperWiseData);
    setChargeDataChanged(true);
  }, [selectAll]);

  const [statusOptions, setStatusOptions] = useState([]);
  const [status, setStatus] = useState(2);
  const [remark, setRemark] = useState("");

  const [statusLoading, setStatusLoading] = useState(false);
  const isSubmitLoading = useSelector((state) => state.invEv.submitLoading);

  const remarkRef = useRef(null);

  useEffect(() => {
    (async () => {
      setStatusLoading(true);
      const { status: statusData } = await getBulkDropdownData([
        { key: "status" },
      ]);
      setStatusOptions(statusData);
      setStatusLoading(false);
    })();
  }, []);

  const memoizedBulkGridData = useMemo(
    () => bulkGridData.filter((data) => !data.hide),
    [bulkGridData],
  );

  const mapGroupData = useCallback(
    (data) => {
      switch (data) {
        case 1: {
          let gridObject = {};
          for (let charge of bulkChargeData) {
            gridObject = {
              ...gridObject,
              [charge.jobCode]: {
                showCharges: false,
                charges: gridObject[charge.jobCode]
                  ? [
                      ...gridObject[charge.jobCode].charges,
                      { ...charge, groupRef: charge.jobCode, hide: true },
                    ]
                  : [{ ...charge, groupRef: charge.jobCode, hide: true }],
              },
            };
          }
          setGridConfig(
            isEditMode ? withTaxConsolidatedViewConfig : consolidatedViewConfig,
          );
          setGroupedData(gridObject);
          break;
        }
        case 2: {
          let gridObject = {};
          for (let charge of bulkChargeData) {
            gridObject = {
              ...gridObject,
              [charge.chgCharges]: {
                showCharges: false,
                charges: gridObject[charge.chgCharges]
                  ? [
                      ...gridObject[charge.chgCharges].charges,
                      { ...charge, groupRef: charge.chgCharges, hide: true },
                    ]
                  : [{ ...charge, groupRef: charge.chgCharges, hide: true }],
              },
            };
          }
          setGridConfig(
            isEditMode ? withTaxConsolidatedViewConfig : consolidatedViewConfig,
          );
          setGroupedData(gridObject);
          break;
        }
        case 3: {
          setGridConfig(
            isEditMode ? noConsolidationViewWithTax : noConsolidationView,
          );
          setBulkGridData(bulkChargeData);
          break;
        }
        case 4: {
          let gridObject = {};
          for (let [shipperId, charges] of Object.entries(shipperWiseData)) {
            gridObject = {
              ...gridObject,
              [shipperId]: {
                showCharges: false,
                charges: charges.map((chg) => ({
                  ...chg,
                  groupRef: shipperId,
                  hide: true,
                })),
              },
            };
          }
          setGridConfig(
            isEditMode ? withTaxConsolidatedViewConfig : consolidatedViewConfig,
          );
          setGroupedData(gridObject);
          break;
        }
        default:
      }
    },
    [bulkChargeData],
  );

  useEffect(() => {
    if (chargeDataChanged) {
      mapGroupData(consolidatedView);
      setChargeDataChanged(false);
    }
  }, [chargeDataChanged, consolidatedView, mapGroupData]);

  const isChargeHeaderRow = (params) => {
    return params.data.section === gridHeaderConst.CHARGE;
  };

  const isChargeHeaderRowConsolidated = (params) => {
    return (
      params.data.section === gridHeaderConst.CHARGE &&
      params.data.consolidateChargeCount > 1
    );
  };

  // Col-span rule

  let chargeCellClassRules = useMemo(
    () => ({
      "header-cell": 'data.section === "CHARGE-HEADER"',
    }),
    [],
  );

  // Custom row selection

  const dataSelection = useCallback((param) => {
    const newValue = !param.data.chdSelected;
    setBulkChargeData((prevState) =>
      prevState.map((chg) => {
        if (chg.chdId === param.data?.chdId) {
          return { ...chg, chdSelected: newValue };
        }
        return chg;
      }),
    );
    setChargeDataChanged(true);
  }, []);

  const groupDataSelection = useCallback((param) => {
    const newValue = !param.data.chdSelected;
    if (isChargeHeaderRow(param)) {
      setGroupedData((prevState) => ({
        ...prevState,
        [param.data?.chgCharges]: {
          ...prevState[param.data?.chgCharges],
          chdSelected: newValue,
          charges: prevState[param.data?.chgCharges].charges.map((chg) => ({
            ...chg,
            chdSelected: newValue,
          })),
        },
      }));
    } else {
      setGroupedData((prevState) => ({
        ...prevState,
        [param.data?.groupRef]: {
          ...prevState[param.data?.groupRef],
          chdSelected: newValue,
          charges: prevState[param.data?.groupRef].charges.map((chg) => {
            if (chg.chdId === param.data?.chdId) {
              return { ...chg, chdSelected: newValue };
            }
            return chg;
          }),
        },
      }));
    }
  }, []);

  // Grid Configs

  const noConsolidationView = useMemo(
    () => [
      {
        headerName: "",
        field: "chdSelected",
        cellRenderer: CheckboxRenderer,
        cellClassRules: chargeCellClassRules,
        cellRendererParams: (param) => ({
          checked: param.data.chdSelected,
          onChange: () => {
            dataSelection(param);
          },
        }),
        headerComponent: (param) => (
          <CheckboxRenderer
            id={2}
            checked={selectAllRef?.current}
            onChange={() => {
              setSelectAll((prevState) => !prevState);
            }}
          />
        ),
      },
      {
        headerName: "Charges",
        field: "chgCharges",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRenderer: ResizableScrollingTextRenderer,
        cellRendererParams: (params) => ({
          threshold: 15,
        }),
      },
      ...commonNoConsolidationView,
    ],
    [chargeCellClassRules, commonNoConsolidationView, dataSelection, selectAll],
  );

  const noConsolidationViewWithTax = useMemo(
    () => [
      ...commonNoConsolidationView,
      {
        headerName: "TAX",
        field: "chdTaxAmount",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRendererSelector: (param) => {
          if (param.data.chdTaxAmount === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
      {
        headerName: "With Tax Total",
        field: "chdTotalWithTax",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (param.data.chdTotalWithTax === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
    ],
    [chargeCellClassRules, commonNoConsolidationView, formatter],
  );

  const consolidatedViewConfig = useMemo(
    () => [
      {
        headerName: "",
        field: "chdSelected",
        cellRenderer: CheckboxRenderer,
        cellClassRules: chargeCellClassRules,
        cellRendererParams: (param) => ({
          checked: param.data.chdSelected,
          onChange: () => {
            groupDataSelection(param);
          },
        }),
        headerComponent: (param) => (
          <CheckboxRenderer
            id={1}
            checked={selectAllRef?.current}
            onChange={() => {
              setSelectAll((prevState) => !prevState);
            }}
          />
        ),
      },
      ...commonConsolidated,
      {
        field: "Show Charges",
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (!isChargeHeaderRowConsolidated(param)) {
            return null;
          }
          return {
            component: ButtonRenderer,
            params: {
              clickHandler: () => {
                setGroupedData((prevState) => ({
                  ...prevState,
                  [param.data.chgCharges]: {
                    ...prevState[param.data.chgCharges],
                    showCharges: !prevState[param.data.chgCharges].showCharges,
                    charges: prevState[param.data.chgCharges].charges.map(
                      (chg) => ({
                        ...chg,
                        hide: !chg.hide,
                      }),
                    ),
                  },
                }));
              },
              label: `${param.data.consolidateChargeCount} Charges`,
              Icon: param.data.showCharges ? ChevronUp : ChevronDown,
              className: "ui primary button",
            },
          };
        },
      },
    ],
    [chargeCellClassRules, commonConsolidated],
  );

  const withTaxConsolidatedViewConfig = useMemo(
    () => [
      ...commonConsolidated,
      {
        headerName: "TAX",
        field: "chdTaxAmount",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (param.data.chdTaxAmount === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
      {
        headerName: "With Tax Total",
        field: "chdTotalWithTax",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (isChargeHeaderRow(param)) {
            if (param.data.chdTaxAmount === -1) {
              return {
                component: () => "Not yet calculated",
              };
            } else {
              return {
                component: CurrencyRenderer,
                params: {
                  formatter: formatter,
                  otherProp: true,
                  currentValue: param.data.chdTaxAmount + param.data.chdTotal,
                },
              };
            }
          }
          if (param.data.chdTotalWithTax === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
      {
        field: "Show Charges",
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (!isChargeHeaderRowConsolidated(param)) {
            return null;
          }
          return {
            component: ButtonRenderer,
            params: {
              clickHandler: () => {
                setGroupedData((prevState) => ({
                  ...prevState,
                  [param.data.chgCharges]: {
                    ...prevState[param.data.chgCharges],
                    showCharges: !prevState[param.data.chgCharges].showCharges,
                    charges: prevState[param.data.chgCharges].charges.map(
                      (chg) => ({
                        ...chg,
                        hide: !chg.hide,
                      }),
                    ),
                  },
                }));
              },
              label: `${param.data.consolidateChargeCount} Charges`,
              Icon: param.data.showCharges ? ChevronUp : ChevronDown,
              className: "ui primary button",
            },
          };
        },
      },
    ],
    [chargeCellClassRules, commonConsolidated, formatter],
  );

  const [gridConfig, setGridConfig] = useState(noConsolidationView);

  useEffect(() => {
    if (consolidatedView !== 3) {
      const currentGridData = [];
      let currentId = 2;
      for (let [header, charges] of Object.entries(groupedData)) {
        currentGridData.push({
          currId: currentId,
          section: gridHeaderConst.CHARGE,
          chgCharges: header,
          chdTotal: charges.charges.reduce(
            (total, charge) => total + charge.chdTotal,
            0,
          ),
          chdTaxAmount: charges.charges.reduce(
            (tax, charge) => tax + charge.chdTaxAmount,
            0,
          ),
          chdTotalWithTax: charges.charges.reduce(
            (tax, charge) => tax + charge.chdTotalWithTax,
            0,
          ),
          chdSelected: charges.charges.every((chg) => chg.chdSelected)
            ? true
            : charges.charges.some((chg) => chg.chdSelected)
              ? null
              : false,
          consolidateChargeCount: charges.charges.length,
          hide: false,
          showCharges: charges.showCharges,
        });
        currentId += 1;
        for (let cuChg of charges.charges) {
          currentGridData.push({ ...cuChg, currId: currentId });
          currentId += 1;
        }
      }
      setBulkGridData(currentGridData);
    }
  }, [consolidatedView, isEditMode, groupedData, bulkChargeData]);

  const actionDropdownHandler = useCallback(
    (data) => {
      mapGroupData(data);
      setConsolidatedView(data);
    },
    [mapGroupData],
  );

  useEffect(() => {
    (async () => {
      setConsolidateOptionsLoading(true);
      const consolidateTypes = await getDropdownData("consolidate_charge");
      setConsolidateOptions(consolidateTypes);
      setConsolidateOptionsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (currentJobIds && currentJobIds.length > 0) {
        setIsLoading(true);
        let jobChargeSummary = {};

        let jobSummaryInfo = {};
        const jobOpt = [];
        const bulkCharges = [];
        const gridDataWithChargeType = [];
        let bulkStakeholder = "";
        let bulkStakeholderId = null;
        let currentCurrency = null;
        let shipperWiseJobs = {};
        for (let { jobId, chgId } of currentJobIds) {
          const currentJobInfoRes = await getJobInfo(jobId);
          const currentJobInfo = currentJobInfoRes[0];

          const invoicingCustomer = currentJobInfo.invoicingCustomer;
          const transporterName = currentJobInfo.transporterName;
          const customerName = currentJobInfo.customerName;

          bulkStakeholder =
            categoryId === 1
              ? isEditMode
                ? savedUpdateResponse.eihInvCustomerName
                : invoicingCustomer
              : isEditMode
                ? savedUpdateResponse.eihTransporterName
                : transporterName;
          bulkStakeholderId =
            categoryId === 1 ? currentJobInfo.invId : currentJobInfo.transId;

          const charges = await getChargesByChhIdAndChargeCategoryNotGenerated(
            chgId,
            categoryId,
          );

          // Fallback validation for invalid status.
          if (
            !isEditMode &&
            ((categoryId === 1 &&
              charges?.jobChargeHeader &&
              charges?.jobChargeHeader?.chhInvStatus !== 3 &&
              charges?.jobChargeHeader?.chhInvStatus !== 6) ||
              (categoryId === 2 &&
                charges?.jobChargeHeader &&
                charges?.jobChargeHeader?.chhEvStatus !== 3 &&
                charges?.jobChargeHeader?.chhEvStatus !== 6))
          ) {
            toast.warn("Charge with invalid status found! Please try again.", {
              autoClose: 5000,
              containerId: "Main",
            });
            dispatch(invEvActions.resetState());
            return;
          }

          jobOpt.push({
            key: chgId,
            text: `${currentJobInfo.jobCode} - ${charges.jobChargeHeader?.chhCode}`,
            value: chgId,
          });

          jobChargeSummary = { ...jobChargeSummary, [chgId]: charges };
          const currencyResponse = charges.jobChargeHeader.chhCurrencyName;
          currentCurrency = charges.jobChargeHeader.chhCurrency;
          jobSummaryInfo = {
            ...jobSummaryInfo,
            [chgId]: [
              {
                title: "EV or INV",
                value: categoryId === 1 ? "INV Generator" : "EV Generator",
              },
              {
                title: categoryId === 1 ? "INV#" : "EV#",
                value: isEditMode
                  ? savedUpdateResponse
                    ? savedUpdateResponse?.eihCode
                    : "N/A"
                  : "N/A",
              },
              { title: "Job No:", value: currentJobInfo.jobCode },
              { title: "Request Code:", value: currentJobInfo.reqCode },
              {
                title: "Customer",
                value: customerName,
              },
              {
                title: "Transporter",
                value: transporterName,
              },
              {
                title: "Shipper",
                value: currentJobInfo.shipperName
                  ? currentJobInfo.shipperName
                  : "N/A",
              },
              {
                title: "Invoicing Customer",
                value: currentJobInfo.invoicingCustomer
                  ? currentJobInfo.invoicingCustomer
                  : "N/A",
              },
              {
                title: "From Date",
                value: currentJobInfo.fromDate
                  ? formatFromJsonDate(currentJobInfo.fromDate)
                  : "N/A",
              },
              {
                title: "To Date",
                value: currentJobInfo.toDate
                  ? formatFromJsonDate(currentJobInfo.toDate)
                  : "N/A",
              },
              {
                title: "Booking Required Date",
                value: currentJobInfo.bookingRequiredDate
                  ? formatFromJsonDate(currentJobInfo.bookingRequiredDate)
                  : "N/A",
              },
              {
                title: "Currency",
                value: currencyResponse ?? "N/A",
              },
              {
                title: "KM",
                value: currentJobInfo.chargeableValue
                  ? `${currentJobInfo.chargeableValue} km`
                  : "N/A",
              },
              {
                title: "Vehicle Type",
                value: currentJobInfo.vhtType ? currentJobInfo.vhtType : "N/A",
              },
              {
                title: "Vehicle No",
                value: currentJobInfo.vehVehicleno
                  ? currentJobInfo.vehVehicleno
                  : "N/A",
              },
              {
                title: "Containers",
                value: currentJobInfo.conNumbers
                  ? currentJobInfo.conNumbers
                  : "N/A",
              },
            ],
          };

          if (isEditMode) {
            const chargeData = await getGridData(savedUpdateResponse, chgId);
            const mappedChargeData = chargeData?.map((data) => ({
              ...data,
              chargeCode: charges.jobChargeHeader.chhCode,
              jobCode: currentJobInfo.jobCode,
              shipper: currentJobInfo.shipperId,
              shipperName: currentJobInfo.shipperName,
            }));

            bulkCharges.push(...mappedChargeData);
            gridDataWithChargeType.push(...mappedChargeData);

            if (categoryId === 1) {
              shipperWiseJobs = {
                ...shipperWiseJobs,
                [currentJobInfo.shipperId]: shipperWiseJobs[
                  currentJobInfo.shipperId
                ]
                  ? [
                      ...shipperWiseJobs[currentJobInfo.shipperId],
                      ...mappedChargeData,
                    ]
                  : mappedChargeData,
              };
            }
          } else {
            let id = 2;
            for (const chargeDetails of charges.jobChargeDetails) {
              const chargeTypeValue = chargeDetails.chdChacrgesValue;
              const chargeStatusName = chargeDetails.chdStatusValue;
              if (categoryId === 1) {
                shipperWiseJobs = {
                  ...shipperWiseJobs,
                  [currentJobInfo.shipperName]: shipperWiseJobs[
                    currentJobInfo.shipperName
                  ]
                    ? [
                        ...shipperWiseJobs[currentJobInfo.shipperName],
                        {
                          ...chargeDetails,
                          id,
                          job: jobId,
                          jobCode: currentJobInfo.jobCode,
                          shipper: currentJobInfo.shipperId,
                          shipperName: currentJobInfo.shipperName,
                          chdTaxAmount: -1,
                          chdSelected: false,
                          chgCharges: chargeTypeValue,
                          chargeStatusName,
                        },
                      ]
                    : [
                        {
                          ...chargeDetails,
                          id,
                          job: jobId,
                          jobCode: currentJobInfo.jobCode,
                          chdTaxAmount: -1,
                          chdSelected: false,
                          chgCharges: chargeTypeValue,
                          shipper: currentJobInfo.shipperId,
                          shipperName: currentJobInfo.shipperName,
                          chargeStatusName,
                        },
                      ],
                };
              }

              bulkCharges.push({
                ...chargeDetails,
                id,
                job: jobId,
                jobCode: currentJobInfo.jobCode,
                shipper: currentJobInfo.shipperId,
                shipperName: currentJobInfo.shipperName,
                chdTaxAmount: -1,
                chdSelected: false,
                chdTotalWithTax: -1,
                chgCharges: chargeTypeValue,
                chargeStatusName,
              });
              gridDataWithChargeType.push({
                id,
                ...chargeDetails,
                job: jobId,
                jobCode: currentJobInfo.jobCode,
                shipper: currentJobInfo.shipperId,
                shipperName: currentJobInfo.shipperName,
                chdTaxAmount: -1,
                chdSelected: false,
                chdTotalWithTax: -1,
                chgCharges: chargeTypeValue,
                chargeStatusName,
              });
              ++id;
            }
          }
        }

        if (isEditMode) {
          dispatch(
            invEvActions.setTaxInfo({
              withoutTaxTotal: savedUpdateResponse.eihWithoutTax,
              withTaxTotal: savedUpdateResponse.eihWithTax,
              taxAmount:
                savedUpdateResponse.eihWithTax -
                savedUpdateResponse.eihWithoutTax,
            }),
          );
          setStatus(savedUpdateResponse.eihStatus);
          setRemark(savedUpdateResponse.eihRemarks);
          remarkRef.current = savedUpdateResponse.eihRemarks;
        }

        dispatch(
          invEvActions.setCurrencyAndStakeholder({
            currencyId: currentCurrency,
            stakeholderId: bulkStakeholderId,
            stakeholderName: bulkStakeholder,
          }),
        );
        setBulkGridData(gridDataWithChargeType);
        setConsolidatedView(3);
        setBulkChargeData(bulkCharges);
        setCurrentStakeholder(bulkStakeholder);
        setShipperWiseData(shipperWiseJobs);
        setBulkJobSummary(jobSummaryInfo);
        setJobOptions(jobOpt);
        setIsLoading(false);
      }
    })();
  }, [categoryId, currentJobIds, savedUpdateResponse, isEditMode, getGridData]);

  const setRequest = async (data) => {
    dispatch(
      invEvActions.setRequest({
        charges: data,
        hasShipperWiseData: Object.entries(shipperWiseData).length > 1,
        shipperWiseData: shipperWiseData,
      }),
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isEditMode) {
      dispatch(
        updateBulkInvEv({
          eihId: savedUpdateResponse.eihId,
          eihRemarks: remark,
          author: username,
        }),
      );
    } else {
      await setRequest(selectedData);
      openTaxBreakdown();
    }
  };

  const onDownload = async () => {
    await addAuditRecord({
      company: company ? company.comId : null,
      action: "Bulk EV/Inv Excel Download",
      params: "",
      type: 6,
      module: 5,
      user: user.username,
    });
    dispatch(
      reportActions.setEvInvoiceReportData({
        jobInfo: null,
        fetchedResponse: savedUpdateResponse,
        moduleCategory: categoryId === 1 ? 4 : 5,
      }),
    );
    openReportModal();
  };

  const onExcelDownload = async () => {
    // Set a loading state before starting the download
    setIsLoading(true);
    const payload = {
      companyProfileSeq: 3,
      reportSeq: 57,
      reportTypeSeq: 5,
      evInvoiceId: savedUpdateResponse?.eihId,
    };

    try {
      const response = await downloadExcelReport(payload);

      if (!response || !response.base64File) {
        console.error("No valid file data returned");
        setIsLoading(false);
        return;
      }

      const byteCharacters = atob(response.base64File);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }

      const blob = new Blob(byteArrays, {
        type:
          response.contentType ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadName = response.displayName || "EV_Invoice_Report.xlsx";

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${downloadName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Successfully downloaded Excel file", {
        autoClose: 5000,
        containerId: "Main",
      });
    } catch (e) {
      console.error("Failed to download Excel:", e);
    } finally {
      // Reset loading state after download attempt
      setIsLoading(false);
    }
  };

  console.log(memoizedBulkGridData);

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="ui form">
            <div className="two fields">
              <Input
                readOnly={true}
                type="text"
                label={categoryId === 1 ? "Customer" : "Transporter"}
                placeholder={categoryId === 1 ? "Customer" : "Transporter"}
                value={currentStakeholder}
              />
              <Dropdown
                clearable={false}
                label="Jobs"
                placeholder="Jobs"
                options={jobOptions}
                value={selectedJob}
                onChange={(e, data) => {
                  dispatch(invEvActions.setSelectedJob(data.value));
                }}
              />
            </div>
          </div>

          <InfoList
            content={bulkJobSummary[selectedJob]}
            title={isEditMode ? "EV/Invoice Summary" : "Charges Summary"}
          />
          {isEditMode && (
            <ChargeTaxBreakdown
              formatter={formatter}
              selectedCharge={selectedCharge}
            />
          )}
          <div className="ui segment">
            <Grid
              module={MODULES.Finance}
              cellClickHandler={(e) => {
                setSelectedCharge(e.data);
              }}
              ref={gridRef}
              showActionMenu={true}
              actionMenuOptions={
                categoryId === 1 && Object.entries(shipperWiseData).length > 1
                  ? [
                      ...consolidateOptions,
                      { key: 4, text: "Shipper", value: 4 },
                    ]
                  : consolidateOptions
              }
              actionMenuLoading={consolidateOptionsLoading}
              dropdown={true}
              dropdownValue={consolidatedView}
              dropdownOptions={{
                label: "Consolidated View",
                multiple: false,
                clearable: false,
                search: false,
              }}
              heading={`${isEditMode ? "" : "Generate"} ${categoryId === 1 ? "Invoice" : "Expense Voucher"} (${categoryId === 1 ? "INV" : "EV"})`}
              handleActionMenuChange={(e, data) => {
                actionDropdownHandler(data.value);
              }}
              content={memoizedBulkGridData}
              config={gridConfig}
            />
            {isEditMode && (
              <div className="ui form" style={{ marginTop: "10px" }}>
                <div className="three fields">
                  <Input
                    field={"eih_without_tax"}
                    module={MODULES.Finance}
                    editMode={isEditMode}
                    form={HEADER_FORM}
                    type="text"
                    label="Without Tax"
                    placeholder="Without Tax"
                    readOnly={true}
                    value={
                      formatter
                        ? formatter.format(withoutTaxTotal)
                        : withTaxTotal
                    }
                  />
                  <Input
                    field={"eih_with_tax"}
                    module={MODULES.Finance}
                    editMode={isEditMode}
                    form={HEADER_FORM}
                    type="text"
                    label="Tax"
                    placeholder="Tax"
                    readOnly={true}
                    value={formatter ? formatter.format(taxAmount) : taxAmount}
                  />
                  <Input
                    field={"eih_total"}
                    module={MODULES.Finance}
                    editMode={isEditMode}
                    form={HEADER_FORM}
                    type="text"
                    label="Total"
                    placeholder="Total"
                    readOnly={true}
                    value={
                      formatter ? formatter.format(withTaxTotal) : withTaxTotal
                    }
                  />
                </div>
                <div className="two fields">
                  <Input
                    required={isEditMode}
                    field={"eih_remarks"}
                    module={MODULES.Finance}
                    editMode={isEditMode}
                    form={HEADER_FORM}
                    authenticated={true}
                    type="text"
                    label="Remarks"
                    placeholder="Remarks"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                  />

                  <Dropdown
                    field={"eih_status"}
                    module={MODULES.Finance}
                    editMode={isEditMode}
                    form={HEADER_FORM}
                    authenticated={true}
                    required={true}
                    clearable={false}
                    readOnly={true}
                    label="Status"
                    placeholder={statusLoading ? "Loading ..." : "Status"}
                    options={statusOptions}
                    style={{ height: "20px" }}
                    value={status}
                    onChange={(e, data) => {
                      setStatus(data.value);
                    }}
                  />
                </div>
              </div>
            )}
            <div
              className="two fields"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                marginTop: "10px",
              }}
            >
              <Button
                template="create"
                isLoading={isSubmitLoading}
                create={!isEditMode}
                onClick={onSubmit}
                form={HEADER_FORM}
                module={MODULES.Finance}
                authenticated={true}
                editMode={isEditMode}
                disabled={
                  (categoryId === 1 && !bulkGenInvPermission) ||
                  (categoryId === 2 && !bulkGenEvPermission) ||
                  (selectedData.length <= 0 &&
                    (!isEditMode || remark === remarkRef.current))
                }
              />

              <Button
                label="Download EV/INV"
                onClick={onDownload}
                disabled={
                  !isEditMode ||
                  (categoryId === 1 && !bulkDownloadInvPermission) ||
                  (categoryId === 2 && !bulkDownloadEvPermission)
                }
              />
              <Button
                label="Download Excel"
                onClick={onExcelDownload}
                disabled={
                  !isEditMode ||
                  (categoryId === 1 && !bulkDownloadInvPermission) ||
                  (categoryId === 2 && !bulkDownloadEvPermission)
                }
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BulkEvInvComponent;
